<template>
  <v-dialog
    v-model="show"
    persistent
    max-width="700px"
  >
    <v-card>
      <v-card-title>
        <span class="text-h5">Выдача пропуска</span>
      </v-card-title>
      <v-card-text>
        <v-autocomplete
          v-model="form.place_id"
          :items="places"
          label="Место *"
          dense
          outlined
          no-data-text="Нет мест"
        />
        <v-text-field
          v-model="form.subject_ref"
          label="Субъект (subject_ref) *"
          dense
          outlined
        />
        <v-combobox
          v-model="form.card_uids"
          :items="cardItems"
          chips
          deletable-chips
          multiple
          dense
          outlined
          label="UID карт *"
          hint="Выберите карту субъекта или введите новый UID, Enter для добавления"
          persistent-hint
          no-data-text="Нет карт субъекта"
        />
        <v-select
          v-model="form.grant_type"
          :items="grantOptions"
          label="Тип права"
          dense
          outlined
        />
        <v-select
          v-model="form.state"
          :items="stateOptions"
          label="Состояние"
          dense
          outlined
        />
        <v-text-field
          v-model="form.role_code"
          label="Код роли"
          dense
          outlined
        />
        <v-text-field
          v-model="form.group_ref"
          label="Группа"
          dense
          outlined
        />
        <v-text-field
          v-model="form.schedule_id"
          label="ID расписания (schedule_id)"
          dense
          outlined
        />
        <v-row dense>
          <v-col
            cols="12"
            sm="6"
          >
            <skud-date-field
              v-model="form.valid_from"
              label="Действует с"
            />
          </v-col>
          <v-col
            cols="12"
            sm="6"
          >
            <skud-date-field
              v-model="form.valid_to"
              label="Действует по"
            />
          </v-col>
        </v-row>
        <skud-point-select
          v-model="form.point_ids"
          :place-id="form.place_id"
          label="Точки (point_ids)"
        />
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn
          color="primary darken-1"
          outlined
          rounded
          small
          class="ml-2 mb-2"
          :loading="loading"
          @click="save"
        >
          <span class="mx-4">Сохранить</span>
        </v-btn>
        <v-btn
          color="secondary darken-1"
          outlined
          rounded
          small
          class="ml-2 mb-2"
          @click="show = false"
        >
          <span class="mx-4">Отмена</span>
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import moment from 'moment'
import { SkudCardService } from '@/views/skud/shared/services/skud-card-service'
import { loadPlaces } from '@/views/skud/shared/services/skud-places-service'
import { GrantType, SubjectState, enumOptions } from '@/views/skud/shared/models/enums'
import SkudPointSelect from '@/views/skud/components/SkudPointSelect.vue'
import SkudDateField from '@/views/skud/components/SkudDateField.vue'

// «Визит»/«Гость» выдаются через диалоги визитов и гостей, в ручной выдаче пропуска недоступны.
const grantOptions = enumOptions(GrantType).filter(o => ![2, 3].includes(o.value))
// В upsert допустимы только active/blocked (0/1), «архив» не выставляется.
const stateOptions = enumOptions(SubjectState).filter(o => o.value <= 1)

export default {
  name: 'SkudPassUpsertDialog',
  components: {
    SkudPointSelect,
    SkudDateField
  },
  data () {
    return {
      svc: null,
      show: false,
      loading: false,
      places: [],
      cardItems: [],
      grantOptions: grantOptions,
      stateOptions: stateOptions,
      form: {
        subject_ref: '',
        card_uids: [],
        grant_type: 0,
        state: 0,
        role_code: '',
        group_ref: '',
        schedule_id: '',
        valid_from: '',
        valid_to: '',
        point_ids: []
      }
    }
  },
  mounted () {
    this.svc = new SkudCardService()
    loadPlaces().then(list => { this.places = list })
  },
  methods: {
    open (subjectRef, cards) {
      this.cardItems = [...new Set((cards || []).map(c => c.uid))]
      this.form = {
        place_id: this.$store.state.skud_place || null,
        subject_ref: subjectRef || '',
        card_uids: [],
        grant_type: 0,
        state: 0,
        role_code: '',
        group_ref: '',
        schedule_id: '',
        valid_from: '',
        valid_to: '',
        point_ids: []
      }
      this.show = true
    },
    async save () {
      if (!this.form.subject_ref) {
        this.$store.commit('snackbar', 'Укажите субъекта')
        return
      }
      if (!this.form.card_uids.length) {
        this.$store.commit('snackbar', 'Добавьте хотя бы один UID карты')
        return
      }
      if (this.form.card_uids.some(u => !u || u.length > 128)) {
        this.$store.commit('snackbar', 'UID карты — от 1 до 128 символов')
        return
      }
      const placeId = this.form.place_id
      if (!placeId) {
        this.$store.commit('snackbar', 'Выберите место')
        return
      }
      const body = {
        subject_ref: this.form.subject_ref,
        role_code: this.form.role_code || null,
        group_ref: this.form.group_ref || null,
        state: this.form.state,
        // CardUids по контракту — объекты {uid, hash_pan?}; hash_pan фронт не считает.
        card_uids: this.form.card_uids.map(u => ({ uid: u.trim() })),
        grant_type: this.form.grant_type,
        schedule_id: this.form.schedule_id || null,
        valid_from: this.form.valid_from ? moment(this.form.valid_from).toISOString() : null,
        valid_to: this.form.valid_to ? moment(this.form.valid_to).toISOString() : null,
        point_ids: this.form.point_ids
      }
      this.loading = true
      const res = await this.svc.upsertPass(placeId, body)
      this.loading = false
      if (res.ok) {
        this.$store.commit('snackbar', 'Пропуск сохранён, rev ' + res.data.revision)
        this.$emit('saved')
        this.show = false
      } else {
        this.$store.commit('snackbar', res.error)
      }
    }
  }
}
</script>
